import React, { useState, useEffect } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  REGISTRATION_CREATE,
  REGISTRATION_UPDATE,
  REGISTRATION_GET_LIST_FILTER,
} from "../../graphql/registrations";
import { VEHICLES_LIST } from "../../graphql/vehicles";
import { TextField, Button, MenuItem, Box, Typography, CircularProgress } from "@mui/material";
import { PRIMARY } from "../../theme/palette";
import Iconify from "../../components/iconify";
import FormProvider from '../../components/hook-form/FormProvider';
import RHFTextField from '../../components/hook-form/RHFTextField';
import { useForm } from "react-hook-form";
import { FileInput } from "@8base/file-manager";
import dayjs from 'dayjs';
import EditableFormField from '../../components/editable-form-field';
import DatePickerAuto from '../../components/custom-datepicker/DatePickerAuto';

const RegistrationForm = ({
  registration,
  onClose,
  vehicleId: propVehicleId,
  isEditing: externalIsEditing,
  onToggleEdit,
}) => {
  const [internalIsEditing, setInternalIsEditing] = useState(!registration); // Start in edit mode for new registrations, view mode for existing
  const isEditing = externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;
  const setIsEditing = onToggleEdit || setInternalIsEditing;
  
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const { data: vehiclesData } = useQuery(VEHICLES_LIST);
  const { data: registrationData, loading: registrationLoading } = useQuery(
    REGISTRATION_GET_LIST_FILTER,
    { variables: { id: registration?.id }, skip: !registration?.id }
  );
  const [createRegistration, { loading: creating }] =
    useMutation(REGISTRATION_CREATE);
  const [updateRegistration, { loading: updating }] =
    useMutation(REGISTRATION_UPDATE);

  const methods = useForm({
    defaultValues: {
      registrationNumber: '',
      annualDate: '',
      expiryDate: '',
      registrations: propVehicleId || '',
    },
    mode: 'all',
  });
  const { handleSubmit, setValue, watch, reset } = methods;

  useEffect(() => {
    if (registration?.id && registrationData?.registrationsList?.items?.length) {
      const reg = registrationData.registrationsList.items[0];
      reset({
        registrationNumber: reg.registrationNumber || '',
        annualDate: reg.annualDate || '',
        expiryDate: reg.expiryDate || '',
        registrations: reg.registrations?.id || propVehicleId || '',
      });
      setUploadedDocs(
        reg.document && reg.document.items && reg.document.items.length > 0
          ? reg.document.items.map(doc => ({
              fileId: doc.fileId || doc.id,
              filename: doc.filename,
              public: doc.public,
              downloadUrl: doc.downloadUrl || '',
            }))
          : []
      );
    } else if (!registration) {
      reset({
        registrationNumber: '',
        annualDate: '',
        expiryDate: '',
        registrations: propVehicleId || '',
      });
      setUploadedDocs([]);
    }
  }, [registration, registrationData, propVehicleId, reset]);

  if (registration?.id && registrationLoading) return <CircularProgress />;

  const uploadDoc = (res) => {
    if (Array.isArray(res) && res.length > 0) {
      setUploadedDocs(
        res.map(file => ({
          fileId: file.fileId, // Use fileId from upload response
          filename: file.filename,
          public: file.public,
        }))
      );
    } else {
      setUploadedDocs([]);
    }
  };

  const handleSubmitForm = async (data) => {
    let finalData;
    if (registration) {
      finalData = {
        ...data,
        annualDate: data.annualDate ? dayjs(data.annualDate).format('YYYY-MM-DD') : '',
        expiryDate: data.expiryDate ? dayjs(data.expiryDate).format('YYYY-MM-DD') : '',
        document: uploadedDocs.length
          ? {
              connect: uploadedDocs.map(doc => ({ id: doc.fileId })),
            }
          : undefined,
        registrations: { connect: { id: data.registrations } },
      };
      await updateRegistration({ variables: { filter: { id: registration.id }, data: finalData } });
    } else {
      finalData = {
        ...data,
        annualDate: data.annualDate ? dayjs(data.annualDate).format('YYYY-MM-DD') : '',
        expiryDate: data.expiryDate ? dayjs(data.expiryDate).format('YYYY-MM-DD') : '',
        document: uploadedDocs.length
          ? {
              create: uploadedDocs.map(doc => ({
                fileId: doc.fileId,
                public: doc.public,
                filename: doc.filename,
              })),
            }
          : undefined,
        registrations: { connect: { id: data.registrations } },
      };
      await createRegistration({ variables: { data: finalData } });
    }

    onClose(true);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (registration) {
      // Reset form to original values
      const reg = registrationData.registrationsList.items[0];
      reset({
        registrationNumber: reg.registrationNumber || '',
        annualDate: reg.annualDate || '',
        expiryDate: reg.expiryDate || '',
        registrations: reg.registrations?.id || propVehicleId || '',
      });
      setUploadedDocs(
        reg.document && reg.document.items && reg.document.items.length > 0
          ? reg.document.items.map(doc => ({
              fileId: doc.fileId || doc.id,
              filename: doc.filename,
              public: doc.public,
              downloadUrl: doc.downloadUrl || '',
            }))
          : []
      );
      setIsEditing(false);
    } else {
      onClose();
    }
  };

  const vehicleOptions = vehiclesData?.vehiclesList?.items?.map(vehicle => ({
    value: vehicle.id,
    label: `${vehicle.make} ${vehicle.model} (${vehicle.licensePlate})`
  })) || [];

  const formValues = watch();

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(handleSubmitForm)}>
      <Box sx={{ mt: 2 }}>

        {/* Edit Button for View Mode - Top */}
        {registration && !isEditing && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button onClick={handleEdit} variant="contained" color="primary">
              Edit
            </Button>
          </Box>
        )}

        {isEditing || !registration ? (
          <>
          {!propVehicleId && (
              <RHFTextField
                select
                label="Vehicle"
                name="registrations"
                fullWidth
                margin="normal"
                required
              >
                {vehiclesData?.vehiclesList?.items.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    {v.make} {v.model} ({v.licensePlate})
                  </MenuItem>
                ))}
              </RHFTextField>
            )}
            
            <RHFTextField
              label="Registration Number"
              name="registrationNumber"
              fullWidth
              margin="normal"
              required
            />
            <DatePickerAuto
              editTab={isEditing}
              rest={{}}
              value={watch('annualDate')}
              label="Annual Date"
              sx={{
                width: '100%',
                mt: "16px",
                mb: "8px",
              }}
              onUpdate={(date) => {
                setValue('annualDate', date);
              }}
              required
            />
            <DatePickerAuto
              editTab={isEditing}
              rest={{}}
              value={watch('expiryDate')}
              label="Expiry Date"
              sx={{
                width: '100%',
                mt: "16px",
                mb: "8px",
              }}
              onUpdate={(date) => {
                setValue('expiryDate', date);
              }}
              required
            />
            <Box sx={{ mt: 2, mb: 1 }}>
              <FileInput
                apiKey={process.env.REACT_APP_API_TOKEN}
                workspace={process.env.REACT_APP_WORKSPACE}
                environment={process.env.REACT_APP_ENVIRONMENT}
                useFilestack={false}
                useS3={true}
                maxFiles={10}
                onChange={uploadDoc}
              >
                {({ pick }) => (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                      }}
                    >
                      <Typography fontWeight={600} sx={{ marginBottom: '10px' }}>
                      Upload Document
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          width: "100%",
                          borderRadius: "8px",
                          height: uploadedDocs.length > 0 ? `${uploadedDocs.length * 40 + 40}px` : "80px",
                          border: "1px dashed gray",
                          justifyContent: "center",
                          background: PRIMARY.gray,
                          ":hover": {
                            background: PRIMARY.lightBlue,
                            cursor: "pointer",
                          },
                          flexDirection: "column",
                          alignItems: "center",
                          overflowY: "auto"
                        }}
                        alignItems="center"
                        onClick={() => {
                          pick();
                        }}
                      >
                        {uploadedDocs.length > 0 ? (
                          uploadedDocs.map((doc, idx) => (
                            <Box key={idx} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                              <Iconify icon="bx:file" height="35px" width="35px" />
                              <Typography
                                sx={{
                                  width: "250px",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {doc.filename}
                              </Typography>
                            </Box>
                          ))
                        ) : (
                          <Iconify icon="uil:file-upload-alt" height="35px" width="35px" />
                        )}
                      </Box>
                    </Box>
                  </>
                )}
              </FileInput>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">Registration Number</Typography>
              <Typography variant="body1">{formValues.registrationNumber || 'Not specified'}</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">Annual Date</Typography>
              <Typography variant="body1">{formValues.annualDate ? dayjs(formValues.annualDate).format('MM/DD/YYYY') : 'Not specified'}</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">Expiry Date</Typography>
              <Typography variant="body1">{formValues.expiryDate ? dayjs(formValues.expiryDate).format('MM/DD/YYYY') : 'Not specified'}</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">Vehicle</Typography>
              <Typography variant="body1">
                {vehicleOptions.find(v => v.value === formValues.registrations)?.label || 'Not specified'}
              </Typography>
            </Box>
            {uploadedDocs.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">Documents</Typography>
                {uploadedDocs.map((doc, idx) => (
                  <Box key={idx} sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                    <Iconify icon="bx:file" height="20px" width="20px" />
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      {doc.filename}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}

        {/* Action Buttons - Bottom */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button onClick={handleCancel} sx={{ mr: 1 }} disabled={creating || updating}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={creating || updating}>
            {registration ? 'Update' : 'Create'}
          </Button>
        </Box>
      </Box>
    </FormProvider>
  );
};

export default RegistrationForm;

